import React, { useEffect, useRef, useState } from 'react';
import type { ChatMessage, QuestionNote } from '../types';
import { formatTime, requestJson, newRequestId } from '../utils';
import ConfirmDialog from './ConfirmDialog';
import MarkdownRenderer from './MarkdownRenderer';

export default function QuestionNotes({ questionId, excerpt, onExcerptUsed }: {
  questionId: number; excerpt?: ChatMessage | null; onExcerptUsed: () => void;
}) {
  const [notes, setNotes] = useState<QuestionNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draft, setDraft] = useState<{ content: string; note?: QuestionNote; source?: ChatMessage; key: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [failedSave, setFailedSave] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState<QuestionNote | null>(null);
  const lock = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  async function load() {
    setLoading(true); setLoadFailed(false);
    try {
      const items = await requestJson<QuestionNote[]>(`/questions/${questionId}/notes`);
      if (alive.current) setNotes(items);
    } catch (err) { if (alive.current) { setLoadFailed(true); setError(err instanceof Error ? err.message : '笔记加载失败'); } }
    finally { if (alive.current) setLoading(false); }
  }
  useEffect(() => { void load(); /* keyed by questionId at the call site */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!excerpt) return;
    if (busy || draft) { onExcerptUsed(); setStatus('请先完成当前笔记编辑，再选择新的摘录。'); return; }
    setDraft({ content: excerpt.message, source: excerpt, key: newRequestId() });
    setError(''); setStatus(''); setFailedSave(false); onExcerptUsed();
  }, [excerpt, busy, draft, onExcerptUsed]);

  function edit(note?: QuestionNote) {
    setDraft({ content: note?.content ?? '', note, key: newRequestId() });
    setFailedSave(false); setStatus(''); setError('');
  }
  async function save() {
    if (!draft || lock.current || !draft.content.trim()) return;
    lock.current = true; setBusy(true); setError(''); setStatus('');
    const source = draft.source;
    try {
      const saved = await requestJson<QuestionNote>(`/questions/${questionId}/notes${draft.note ? `/${draft.note.id}` : ''}`, {
        method: draft.note ? 'PATCH' : 'POST', body: JSON.stringify(draft.note
          ? { content: draft.content, version: draft.note.version }
          : { content: draft.content, idempotencyKey: draft.key, sourceKind: source ? 'ai' : 'personal',
              sourceMessageId: source && !source.synthetic && source.id > 0 && source.createdAt ? source.id : undefined,
              sourceLabel: source ? (source.synthetic ? 'AI 解析摘要' : source.id < 0 ? 'AI 动作摘录' : 'AI 回答摘录') : undefined,
              sourceSnapshot: source?.message }),
      });
      if (!alive.current) return;
      setNotes(prev => [saved, ...prev.filter(item => item.id !== saved.id)]);
      setDraft(null); setFailedSave(false); setStatus('笔记已保存');
    } catch (err) { if (alive.current) { setFailedSave(true); setError(err instanceof Error ? err.message : '保存失败，请重试'); } }
    finally { lock.current = false; if (alive.current) setBusy(false); }
  }
  async function remove() {
    if (!deleting || lock.current) return;
    lock.current = true; setBusy(true); setError(''); setStatus('');
    try {
      await requestJson(`/questions/${questionId}/notes/${deleting.id}?version=${deleting.version}`, { method: 'DELETE' });
      if (!alive.current) return;
      setNotes(prev => prev.filter(item => item.id !== deleting.id)); setDeleting(null); setStatus('笔记已删除');
    } catch (err) { if (alive.current) setError(err instanceof Error ? err.message : '删除失败，请重试'); }
    finally { lock.current = false; if (alive.current) setBusy(false); }
  }
  return <section className="question-notes" aria-label="本题笔记">
    <div className="option-edit-header"><h3>本题笔记</h3><button type="button" className="text-button" disabled={busy || !!draft || loading || loadFailed} onClick={() => edit()}>新增笔记</button></div>
    {loading ? <p role="status">笔记加载中…</p> : !loadFailed && notes.length === 0 ? <p className="empty-state">还没有笔记，记下自己的理解或收藏一条 AI 回答。</p> : null}
    {loadFailed ? <button type="button" className="text-button" onClick={load}>重试加载笔记</button> : null}
    {status ? <p role="status">{status}</p> : null}
    {error ? <p role="alert">{error}</p> : null}
    {draft ? <div className="edit-form">
      <span>{draft.note ? '编辑笔记' : draft.source ? '编辑 AI 摘录后保存' : '个人笔记'}</span>
      <textarea autoFocus aria-label="笔记内容" className="form-input form-textarea" rows={7} maxLength={20000} value={draft.content} disabled={busy || failedSave} onChange={e => setDraft({ ...draft, content: e.target.value })} />
      {failedSave ? <p>保存未确认，请重试。若提示版本冲突，请取消并刷新笔记后编辑。</p> : null}
      <div className="note-actions"><button type="button" className="filled-button" disabled={loading || loadFailed || busy || !draft.content.trim() || Array.from(draft.content).length > 20000} onClick={save}>{busy ? '保存中…' : failedSave ? '重试保存笔记' : '保存笔记'}</button>
      <button type="button" className="text-button" disabled={busy} onClick={() => { setDraft(null); setFailedSave(false); }}>取消编辑笔记</button></div>
    </div> : null}
    {notes.map(note => <article className="note-card" key={note.id}>
      <div className="section-meta">{note.sourceKind === 'ai' ? `AI 摘录 · ${note.sourceLabel || 'AI 回答'}${note.sourceMessageId ? ` #${note.sourceMessageId}` : ''}` : '个人笔记'} · {formatTime(note.updatedAt)}</div>
      <MarkdownRenderer content={note.content} />
      {note.sourceKind === 'ai' ? <details><summary>查看摘录原文</summary><pre className="note-source">{note.sourceSnapshot}</pre></details> : null}
      <div className="note-actions"><button type="button" className="text-button" disabled={busy || !!draft} onClick={() => edit(note)}>编辑笔记</button><button type="button" className="text-button" disabled={busy || !!draft} onClick={() => setDeleting(note)}>删除笔记</button></div>
    </article>)}
    {!draft && !loading ? <button type="button" className="text-button" disabled={busy} onClick={() => { setError(''); void load(); }}>刷新笔记</button> : null}
    {deleting ? <ConfirmDialog title="删除笔记" description="确定删除这条笔记？" confirmText="确认删除笔记" danger loading={busy} onCancel={() => { if (!busy) setDeleting(null); }} onConfirm={remove} /> : null}
  </section>;
}
